import { useCallback, useEffect, useState } from 'react'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import type { MusicPlaylist } from '@/lib/db'

type PlaylistEditSheetProps = {
  playlist: MusicPlaylist | null
  onClose: () => void
  onRename: (id: string, name: string) => Promise<void>
  onDelete: (id: string) => Promise<void>
}

/**
 * プレイリスト編集ボトムシート（リネーム＆削除）
 */
export const PlaylistEditSheet = ({ playlist, onClose, onRename, onDelete }: PlaylistEditSheetProps) => {
  const [name, setName] = useState('')

  useEffect(() => {
    if (playlist) {
      setName(playlist.name)
    }
  }, [playlist])

  const handleRename = useCallback(async () => {
    if (!playlist) return
    const trimmed = name.trim()
    if (!trimmed) return

    await onRename(playlist.id, trimmed)
    onClose()
  }, [playlist, name, onRename, onClose])

  const handleDelete = useCallback(async () => {
    if (!playlist) return
    await onDelete(playlist.id)
    onClose()
  }, [playlist, onDelete, onClose])

  return (
    <BottomSheet
      title="プレイリストの編集"
      open={!!playlist}
      onOpenChange={(open) => !open && onClose()}
      snapPoints={[0.35]}
    >
      <div className="space-y-4 px-4 pb-4">
        {/* リネーム */}
        <div className="space-y-2">
          <Label htmlFor="rename-input">名前</Label>
          <div className="flex gap-2">
            <Input id="rename-input" value={name} onChange={(e) => setName(e.target.value)} className="flex-1" />
            <Button onClick={handleRename} disabled={!name.trim()}>
              変更
            </Button>
          </div>
        </div>

        <Separator />

        {/* 削除 */}
        <Button variant="destructive" className="w-full" onClick={handleDelete}>
          プレイリストを削除
        </Button>
        <p className="text-xs text-muted-foreground text-center">
          ※ プレイリストのみ削除されます。曲は削除されません。
        </p>
      </div>
    </BottomSheet>
  )
}
